/**
 * Small inline stroke icons sized to sit inside `Button` (which supplies
 * `gap-2` + `items-center`). They inherit color via `currentColor` and are
 * hidden from assistive tech — the button label carries the meaning.
 */
type IconProps = React.SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 16, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const PlusIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const MinusIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 12h14" />
  </Icon>
);

/** Four corners pointing out: "expand to full screen". */
export const ExpandIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </Icon>
);

export const CloseIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

export const CheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 6 9 17l-5-5" />
  </Icon>
);

export const RefreshIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
  </Icon>
);

export const CameraIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
    <circle cx="12" cy="13" r="3.5" />
  </Icon>
);

/** Attach files — paperclip. */
export const PaperclipIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </Icon>
);

/** Receiving: goods coming in — arrow down into a tray. */
export const ArrowDownIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </Icon>
);

/** Shipping: goods going out — arrow up off a tray. */
export const ArrowUpIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 21V9" />
    <path d="m7 14 5-5 5 5" />
    <path d="M5 3h14" />
  </Icon>
);

/** Write-off: goods removed from stock (damaged, lost) — a box crossed out. */
export const WriteOffIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" />
    <path d="M3.3 7 12 12l8.7-5" />
    <path d="M12 22V12" />
    <path d="m17 13 5 5m0-5-5 5" />
  </Icon>
);

/** Cargo flag: fragile — the ISO wine glass. */
export const FragileIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7 3h10l-1.1 5.6a3.9 3.9 0 0 1-7.8 0L7 3Z" />
    <path d="M12 12.5v5.5" />
    <path d="M7.5 21h9" />
  </Icon>
);

/** Cargo flag: keep upright — the ISO "this way up" arrows. */
export const UprightIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.5 20V5" />
    <path d="m5 8.5 3.5-3.5L12 8.5" />
    <path d="M15.5 20V5" />
    <path d="m12 8.5 3.5-3.5L19 8.5" />
  </Icon>
);

/** Cargo flag: do not stack — two boxes struck through. */
export const NonStackableIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="4.5" width="16" height="6" rx="1" />
    <rect x="4" y="13.5" width="16" height="6" rx="1" />
    <path d="M21 3 3 21" />
  </Icon>
);

/** Cargo flag: do not tilt — a box under a turning arrow, struck through. */
export const NonTiltableIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="6" y="10" width="12" height="10" rx="1" />
    <path d="M7.5 6.5a6.5 6.5 0 0 1 9 0" />
    <path d="M16.5 3.5v3h-3" />
    <path d="M21 3 3 21" />
  </Icon>
);

/** Stands in for a file with no image preview (documents, chat attachments). */
export const FileIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
  </Icon>
);

/** Overflow ("more actions") — three dots, filled rather than stroked. */
export const MoreIcon = ({ size = 16, ...rest }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...rest}
  >
    <circle cx="5" cy="12" r="1.8" />
    <circle cx="12" cy="12" r="1.8" />
    <circle cx="19" cy="12" r="1.8" />
  </svg>
);
